import React, { useState, useRef, useEffect } from 'react';

import styles from './CodeField.scss';

type Props = {
  value: string;
  onChange: (value: string) => void;
  cellCount?: number;
  disabled?: boolean;
};

const normalizeValueToArray = (value: string, cellCount: number): string[] => {
  const arr = value.split('');

  while (arr.length < cellCount) {
    arr.push('');
  }

  return arr.slice(0, cellCount);
};

export const CodeField = ({
  value,
  onChange,
  cellCount = 6,
  disabled = false,
}: Props) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const backspacePressedRef = useRef<boolean>(false);

 
  const [internalValue, setInternalValue] = useState<string[]>(() =>
    normalizeValueToArray(value, cellCount)
  );

 
  const lastNotifiedValueRef = useRef<string>('');
  
  const initialInternalValue = normalizeValueToArray(value, cellCount);
  const internalValueRef = useRef<string[]>(initialInternalValue);

  // Keep ref in sync with state
  useEffect(() => {
    internalValueRef.current = internalValue;
  }, [internalValue]);

  useEffect(() => {
    // If parent value is empty, reset internal state
    if (value === '') {
      const emptyArray = Array(cellCount).fill('');

      setInternalValue(emptyArray);
      internalValueRef.current = emptyArray;
      lastNotifiedValueRef.current = '';

      return;
    }

    
    if (value !== lastNotifiedValueRef.current) {
      if (!/^\d+$/.test(value)) {
        return;
      }

      
      if (value.length === cellCount) {
        const arr = value.split('');

        setInternalValue(arr);
        internalValueRef.current = arr;
        lastNotifiedValueRef.current = value;

        return;
      }

      
      const currentNonEmpty = internalValueRef.current.filter((char) => char !== '').join('');

      if (currentNonEmpty === value) {
        
        lastNotifiedValueRef.current = value;

        return;
      }

      
      const newInternalValue = Array(cellCount).fill('');
      const digits = value.split('');

      for (let i = 0; i < digits.length && i < cellCount; i++) {
        newInternalValue[i] = digits[i];
      }

      setInternalValue(newInternalValue);
      internalValueRef.current = newInternalValue;
      lastNotifiedValueRef.current = value;
    }
  }, [value, cellCount]);

  
  const notifyParent = (newInternalValue: string[]) => {
    // Filter out empty strings and join - this gives us the actual code digits
    const codeString = newInternalValue.filter((char) => char !== '').join('');

    lastNotifiedValueRef.current = codeString;
    onChange(codeString);
  };

  // Auto-focus first input when component mounts
  useEffect(() => {
    if (inputRefs.current[0] && !disabled) {
      inputRefs.current[0].focus();
    }
  }, [disabled]);

  // Auto-blur when all cells are filled
  useEffect(() => {
    const filledCount = internalValue.filter((char) => char !== '').length;

    if (
      filledCount === cellCount &&
      inputRefs.current &&
      inputRefs.current[cellCount - 1]
    ) {
      inputRefs.current[cellCount - 1]?.blur();
    }
  }, [internalValue, cellCount]);

  const handleInputChange = (index: number, inputValue: string) => {
    
    const digits = inputValue.replace(/\D/g, '');
    const singleDigit = digits.length > 0 ? digits.slice(-1) : '';

    if (singleDigit) {
      
      const newInternalValue = [...internalValue];

      newInternalValue[index] = singleDigit;

      setInternalValue(newInternalValue);

      
      if (internalValue[index] !== singleDigit) {
        notifyParent(newInternalValue);
      }

      
      if (index < cellCount - 1) {
        requestAnimationFrame(() => {
          inputRefs.current[index + 1]?.focus();
        });
      }
      
      backspacePressedRef.current = false;
    } else {
      // Handle backspace or empty input
      if (backspacePressedRef.current || inputValue === '') {
        const newInternalValue = [...internalValue];

        newInternalValue[index] = '';
        setInternalValue(newInternalValue);
        notifyParent(newInternalValue);
       
      } else {
        setInternalValue([...internalValue]);
      }

      // Reset backspace flag
      backspacePressedRef.current = false;
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      // Track that backspace was pressed
      backspacePressedRef.current = true;
      
      if (!internalValue[index] && index > 0) {
        e.preventDefault();
        const newInternalValue = [...internalValue];

        newInternalValue[index - 1] = '';
        setInternalValue(newInternalValue);
        notifyParent(newInternalValue);
        // Move focus to the cell that was just deleted
        inputRefs.current[index - 1]?.focus();
        
        backspacePressedRef.current = false;
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      // Allow arrow key navigation
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < cellCount - 1) {
      // Allow arrow key navigation
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleFocus = (index: number, e: React.FocusEvent<HTMLInputElement>) => {
    setFocusedIndex(index);
    setTimeout(() => {
      e.target.select();
    }, 0);
  };

  const handleClick = (index: number) => {
    const input = inputRefs.current[index];

    if (input) {
      input.focus();
      setTimeout(() => {
        input.select();
      }, 0);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, cellCount);

    if (pastedData.length > 0) {
      // Find which cell was pasted into
      const focusedInput = inputRefs.current.findIndex((ref) => ref === document.activeElement);
      const startIndex = focusedInput >= 0 ? focusedInput : 0;

      const newInternalValue = [...internalValue];

      // Fill cells starting from the focused cell
      for (let i = 0; i < pastedData.length && startIndex + i < cellCount; i++) {
        newInternalValue[startIndex + i] = pastedData[i];
      }

      setInternalValue(newInternalValue);
      notifyParent(newInternalValue);

      // Focus the next empty cell or the last cell
      const nextIndex = Math.min(startIndex + pastedData.length, cellCount - 1);

      inputRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div className={styles.codeField}>
      {Array.from({ length: cellCount }, (_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={internalValue[index] || ''}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onClick={() => handleClick(index)}
          onFocus={(e) => handleFocus(index, e)}
          onBlur={() => setFocusedIndex(null)}
          disabled={disabled}
          className={`${styles.cell} ${
            focusedIndex === index ? styles.focused : ''
          } ${internalValue[index] ? styles.filled : ''}`}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
};